import CancelIcon from "@mui/icons-material/Cancel";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  chipClasses,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { withErrorBoundary } from "hocs/withErrorBoundary";
import useGlobalContext from "hooks/globalData";
import useDebounce from "hooks/useDebounce";
import { useStrictQuery } from "hooks/useStrictQuery";
import { useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useQueryClient } from "react-query";
import {
  BaseFilter,
  ListRequestOfBaseFilter,
  ListResponseOfSkuTag,
  ListResponseOfTag,
  SkuTag,
  SkuTagRequest,
  Tag,
} from "services/KnowfullyAdminClient";
import translateError from "utils/translateError";
import { isOnSku, optionName, responseError, skuRuleAvailableTagsKey } from "pages/SkusPage/InternalTags/skuTags";

const styles = {
  header: {
    display: "flex",
    alignItems: "center",
    gap: 0.5,
    px: 2,
    py: 1,
    bgcolor: "grey.100",
    borderBottom: 1,
    borderColor: "divider",
  },
  title: { color: "text.primary", fontWeight: 500 },
  body: { p: 2 },
  chips: { display: "flex", flexWrap: "wrap", gap: 1, mb: 2 },
  // The remove button takes Chip's own delete icon look; it sits inside the label, so its margins shift by the label padding.
  editableChip: {
    "& .MuiChip-label": { display: "flex", alignItems: "center" },
    "& .MuiChip-label .MuiChip-deleteIcon": { p: 0, ml: 0.5, mr: -0.5, "&:hover": { bgcolor: "transparent" } },
  },
  liveRegion: {
    position: "absolute",
    width: 1,
    height: 1,
    overflow: "hidden",
    clip: "rect(0 0 0 0)",
    whiteSpace: "nowrap",
  },
};

type InternalTagsProps = {
  skuId: string;
  isReadonly?: boolean;
};

const InternalTags = ({ skuId, isReadonly }: InternalTagsProps) => {
  const { formatMessage: t } = useIntl();
  const { skuService } = useGlobalContext();
  const queryClient = useQueryClient();
  const [input, setInput] = useState("");
  const [error, setError] = useState("");
  const [announcement, setAnnouncement] = useState("");
  const [saving, setSaving] = useState(false);
  // A ref for the same reason as `removing`: `saving` reaches the next render only.
  const pending = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // A ref, so a second click in the same tick already sees the first one.
  const removing = useRef(new Set<string>());
  const searchText = useDebounce(input.trim(), 300);

  const tagsKey = ["skuService.getSkuTags", skuId];
  const { data: tagsData } = useStrictQuery<ListResponseOfSkuTag>(
    tagsKey,
    () => skuService.getSkuTags(skuId, ListRequestOfBaseFilter.fromJS({ pageSize: -1 })),
    { enabled: !!skuId }
  );
  const skuTags = tagsData?.data ?? [];

  // The SKU list's tag options and its tag-filtered results are cached too, with no expiry.
  const refreshTagQueries = () =>
    Promise.all([
      queryClient.invalidateQueries(tagsKey),
      queryClient.invalidateQueries("skuService.getAvailableTags"),
      queryClient.invalidateQueries("skusList"),
      queryClient.invalidateQueries(skuRuleAvailableTagsKey),
    ]);

  const { data: suggestionsData, isFetching: searching } = useStrictQuery<ListResponseOfTag>(
    ["skuService.searchTagSuggestions", skuId, searchText],
    () =>
      skuService.searchTagSuggestions(
        skuId,
        ListRequestOfBaseFilter.fromJS({ parameters: BaseFilter.fromJS({ searchText }) })
      ),
    { enabled: !isReadonly && !!skuId && !!searchText }
  );

  const fail = (message: string) => {
    setError(message);
    setAnnouncement(message);
  };

  const add = async (value: string) => {
    const name = value.trim();
    if (!name || pending.current) return;
    setError("");

    if (isOnSku(name, skuTags)) {
      setInput("");
      setAnnouncement(t({ id: "tagNameIsAlreadyAdded" }, { name }));
      return;
    }

    pending.current = true;
    setSaving(true);
    try {
      const failure = responseError(
        await skuService.saveSkuTag(skuId, SkuTagRequest.fromJS({ name })),
        t({ id: "serverError" })
      );
      if (failure) {
        setInput(name);
        fail(failure);
        return;
      }
      setInput("");
      setAnnouncement(t({ id: "tagNameAdded" }, { name }));
      await refreshTagQueries();
    } catch (err) {
      setInput(name);
      fail(translateError(err, t));
    } finally {
      pending.current = false;
      setSaving(false);
    }
  };

  const remove = async (skuTag: SkuTag) => {
    const id = skuTag.id!;
    if (removing.current.has(id)) return;
    removing.current.add(id);
    const name = skuTag.tag?.name ?? "";
    setError("");
    try {
      const failure = responseError(await skuService.deleteSkuTag(id), t({ id: "serverError" }));
      if (failure) {
        fail(failure);
        return;
      }
      setAnnouncement(t({ id: "tagNameRemoved" }, { name }));
      inputRef.current?.focus();
      await refreshTagQueries();
    } catch (err) {
      fail(translateError(err, t));
    } finally {
      removing.current.delete(id);
    }
  };

  return (
    <Paper variant="outlined">
      <Box sx={styles.header}>
        <Typography variant="subtitle1" component="h2" sx={styles.title}>
          {t({ id: "internalTags" })}
        </Typography>
        <Tooltip title={t({ id: "tagsAreInternalOnly" })} describeChild>
          <IconButton size="small" aria-label={t({ id: "aboutTags" })}>
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={styles.body}>
        {skuTags.length ? (
          <Box sx={styles.chips}>
            {skuTags.map((skuTag) =>
              isReadonly ? (
                <Chip key={skuTag.id} label={skuTag.tag?.name} size="small" />
              ) : (
                // Only the X removes: the chip itself is not a control, so clicking the name does nothing.
                <Chip
                  key={skuTag.id}
                  size="small"
                  sx={styles.editableChip}
                  label={
                    <>
                      {skuTag.tag?.name}
                      <IconButton
                        className={chipClasses.deleteIcon}
                        aria-label={t({ id: "removeTagName" }, { name: skuTag.tag?.name })}
                        onClick={() => remove(skuTag)}
                      >
                        <CancelIcon fontSize="inherit" />
                      </IconButton>
                    </>
                  }
                />
              )
            )}
          </Box>
        ) : (
          <Typography variant="body2" sx={{ fontStyle: "italic" }}>
            {t({ id: "noTagsAddedYet" })}
          </Typography>
        )}

        {!isReadonly && (
          <Autocomplete<Tag, false, false, true>
            freeSolo
            options={searchText ? suggestionsData?.data ?? [] : []}
            loading={searching}
            filterOptions={(options) => options}
            getOptionLabel={optionName}
            value={null}
            inputValue={input}
            onInputChange={(_, value) => setInput(value)}
            onChange={(_, value) => value && add(optionName(value))}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                inputRef={inputRef}
                placeholder={t({ id: "addATag" })}
                error={!!error}
                helperText={error || t({ id: "startTypingToSearchExistingTags" })}
                inputProps={{ ...params.inputProps, maxLength: 100, "aria-label": t({ id: "addATag" }) }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button size="small" disabled={!input.trim() || saving} onClick={() => add(input)}>
                        {t({ id: "add" })}
                      </Button>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
        )}
      </Box>

      <Box role="status" aria-live="polite" sx={styles.liveRegion}>
        {announcement}
      </Box>
    </Paper>
  );
};

export default withErrorBoundary(InternalTags);
